import { useEffect, useMemo, useRef, useState } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { getTrans, getTransWithParams } from 'modules/i18n';
import { textFor } from '../localizedText';
import {
  signalMarker,
  signalMono,
  signalOptionDescription,
  signalOptionLabel,
  signalOptionRow,
  signalRevealed,
  signalTextField
} from '../signalStyles';
import { BlockHeading } from './BlockHeading';
import {
  readSingleSelectAnswer,
  readSingleSelectConfig,
  SingleSelectOption
} from './singleSelect';
import { BlockRendererProps, questionIdOf } from './types';

/** Show the character counter only once it starts to matter, not from the first keystroke. */
const COUNTER_VISIBLE_FROM = 0.8;

const optionKeyToIndex = (
  options: SingleSelectOption[],
  optionKey: string | undefined
): number => {
  const index = options.findIndex(option => option.key === optionKey);
  return index >= 0 ? index : 0;
};

/**
 * `single_select@1`: one choice from a list, optionally with a free-text "other".
 *
 * Built as a real radio group rather than a row of buttons, because that is what it is: exactly one
 * answer, arrow keys move between options, Space and Enter choose, and assistive technology
 * announces "2 of 3, selected". Tapping is the primary route and always works; the keyboard is an
 * equivalent, never a fallback (catalog §12).
 *
 * Colour is never the only signal of selection — the marker fills, the label gains weight and
 * `aria-checked` carries it too — and every colour comes from a semantic theme token, so the rebrand
 * moves this component without touching it (D19). The row treatment itself lives in `signalStyles`
 * because the next enumerated types reuse it verbatim.
 */
export const SingleSelectBlock = ({
  block,
  answer,
  onChange,
  locale
}: BlockRendererProps) => {
  const { options, otherTextMaxLength, showDescriptions } = useMemo(
    () => readSingleSelectConfig(block),
    [block]
  );
  const parsed = readSingleSelectAnswer(answer);
  const selectedKey = parsed?.optionKey;
  const otherText = parsed?.otherText ?? '';

  const [focusIndex, setFocusIndex] = useState(() =>
    optionKeyToIndex(options, selectedKey)
  );
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const otherFieldRef = useRef<HTMLInputElement | null>(null);
  const questionId = questionIdOf(block);

  const selectedOption = options.find(option => option.key === selectedKey);
  const isOtherSelected = selectedOption?.kind === 'OTHER';

  // Move focus into the text field when "other" becomes the answer, so the extra step the choice
  // just created is where the cursor already is. Skipped on first render: a resumed answer must not
  // yank focus (and the keyboard open) before anyone has interacted.
  const previouslyOther = useRef(isOtherSelected);
  useEffect(() => {
    if (isOtherSelected && !previouslyOther.current) {
      otherFieldRef.current?.focus();
    }
    previouslyOther.current = isOtherSelected;
  }, [isOtherSelected]);

  const select = (option: SingleSelectOption) => {
    // Changing choice drops any text belonging to the previous one: keeping it would let an answer
    // carry free text the server refuses for a non-"other" option.
    onChange({
      // `|| null` rather than an empty string: an untouched "other" should read as no text at all,
      // which is also how the server distinguishes "chose other" from "chose other and told us".
      otherText: option.kind === 'OTHER' ? otherText || null : null,
      optionKey: option.key
    });
  };

  const moveFocus = (delta: number) => {
    if (!options.length) {
      return;
    }
    const next = (focusIndex + delta + options.length) % options.length;
    setFocusIndex(next);
    optionRefs.current[next]?.focus();
    // Arrow keys select as they move — the native radio-group behaviour people already expect.
    select(options[next]);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      event.preventDefault();
      moveFocus(1);
      return;
    }
    if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      event.preventDefault();
      moveFocus(-1);
    }
  };

  const remaining = otherTextMaxLength - otherText.length;
  const showCounter =
    otherText.length >= otherTextMaxLength * COUNTER_VISIBLE_FROM;

  return (
    <Stack spacing={3}>
      {/* The radio shape says "one of these" to anyone who has met a form before — but it says it
          only to someone who notices the shape, and two words cost less than a mis-answer. */}
      <BlockHeading
        block={block}
        locale={locale}
        hint={getTrans('guestForm.singleSelect.hint')}
      />

      <Box
        role="radiogroup"
        aria-labelledby={questionId}
        aria-required={block.required}
        onKeyDown={handleKeyDown}
        sx={{ display: 'flex', flexDirection: 'column', gap: '9px' }}
      >
        {options.map((option, index) => {
          const isSelected = option.key === selectedKey;
          const description = showDescriptions
            ? textFor(option.description, locale)
            : '';
          return (
            <ButtonBase
              key={option.key}
              ref={element => {
                optionRefs.current[index] = element;
              }}
              role="radio"
              aria-checked={isSelected}
              tabIndex={index === focusIndex ? 0 : -1}
              onClick={() => {
                setFocusIndex(index);
                select(option);
              }}
              data-testid={`form-option-${option.key}`}
              sx={signalOptionRow(isSelected, option.kind === 'OTHER')}
            >
              <Box aria-hidden="true" sx={signalMarker(isSelected)}>
                <CheckIcon />
              </Box>
              <Box sx={signalOptionLabel}>
                {textFor(option.label, locale)}
                {description && (
                  <Box sx={signalOptionDescription}>{description}</Box>
                )}
              </Box>
            </ButtonBase>
          );
        })}
      </Box>

      {isOtherSelected && (
        <Box sx={signalRevealed}>
          <TextField
            inputRef={otherFieldRef}
            value={otherText}
            onChange={event =>
              onChange({
                optionKey: selectedKey,
                otherText: event.target.value
              })
            }
            label={getTrans('guestForm.other.label')}
            inputProps={{
              maxLength: otherTextMaxLength,
              'data-testid': 'form-other-text'
            }}
            helperText={
              showCounter
                ? getTransWithParams('guestForm.other.remaining', { remaining })
                : ' '
            }
            // Tabular numerals so a counting-down number does not shift the line under the field.
            FormHelperTextProps={{ sx: signalMono }}
            sx={signalTextField}
            fullWidth
            multiline
            minRows={2}
          />
        </Box>
      )}
    </Stack>
  );
};
